import { Pencil, Check, X, Trash2 } from "lucide-react";
import { formatINR } from "@/lib/quotationApi";

/**
 * A single (finish × tier) price cell with view / inline-edit modes.
 * View: shows effective ₹/sqft, muted "(base)" hint when the non-base tier
 * inherits the finish's base price.
 * Edit: number input + save/cancel; clear-override trash for non-base tiers.
 */
export default function FinishPriceCell({
  finish,
  tier,
  isEditing,
  draft,
  onDraftChange,
  onStartEdit,
  onSave,
  onCancel,
  onClearOverride,
}) {
  const override = finish.tier_prices?.[tier.id];
  const hasOverride = override != null && override !== "";
  const displayValue = tier.is_base
    ? finish.price_per_sqft
    : hasOverride
      ? override
      : finish.price_per_sqft;
  const isInherited = !tier.is_base && !hasOverride;

  return (
    <td
      className="px-3 py-3 border-l border-white/5 align-top"
      data-testid={`cell-${finish.id}-${tier.id}`}
    >
      {isEditing ? (
        <div className="flex items-center gap-1">
          <input
            type="number"
            className="input-luxe !h-8 !text-sm !px-2 flex-1 min-w-0"
            value={draft}
            autoFocus
            inputMode="numeric"
            onChange={(e) => onDraftChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") onSave();
              if (e.key === "Escape") onCancel();
            }}
            data-testid={`cell-input-${finish.id}-${tier.id}`}
          />
          <button
            onClick={onSave}
            className="w-7 h-7 rounded-md text-[#C5A880] hover:bg-white/10 shrink-0"
            data-testid={`save-cell-${finish.id}-${tier.id}`}
            aria-label="Save price"
          >
            <Check size={13} />
          </button>
          <button
            onClick={onCancel}
            className="w-7 h-7 rounded-md text-white/50 hover:bg-white/10 shrink-0"
            aria-label="Cancel"
          >
            <X size={13} />
          </button>
          {!tier.is_base && hasOverride && (
            <button
              onClick={onClearOverride}
              className="w-7 h-7 rounded-md text-white/40 hover:text-red-400 hover:bg-white/10 shrink-0"
              data-testid={`clear-cell-${finish.id}-${tier.id}`}
              aria-label="Clear override"
              title="Clear override (use base price)"
            >
              <Trash2 size={12} />
            </button>
          )}
        </div>
      ) : (
        <button
          onClick={onStartEdit}
          className="group w-full flex items-center justify-between gap-2 text-left rounded-md px-1.5 py-1 -mx-1.5 -my-1 hover:bg-white/5"
          data-testid={`cell-btn-${finish.id}-${tier.id}`}
        >
          <span
            className={`text-sm ${isInherited ? "text-white/40 italic" : "text-white"}`}
          >
            {formatINR(displayValue)}
            {isInherited && (
              <span className="ml-1 text-[9px] text-white/30">(base)</span>
            )}
          </span>
          <Pencil
            size={11}
            className="text-white/25 group-hover:text-[#C5A880] shrink-0"
          />
        </button>
      )}
    </td>
  );
}
